import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// localStorage 键名
const LOCK_SESSION_KEY = 'note_lock_session'

// 默认配置
const DEFAULT_CONFIG = {
  idleTimeout: 10 * 60 * 1000,      // 10分钟空闲超时
  absoluteTimeout: 30 * 60 * 1000,  // 30分钟绝对超时
  warningBeforeTimeout: 60 * 1000,  // 超时前1分钟警告
  resetOnMidnight: true,             // 跨天重置
}

export interface LockSessionState {
  isGloballyUnlocked: boolean      // 是否全局解锁
  unlockedAt: number | null        // 解锁时间戳
  lastActivityAt: number | null    // 最后活动时间戳
  config: {
    idleTimeout: number
    absoluteTimeout: number
    warningBeforeTimeout: number
    resetOnMidnight: boolean
  }
}

// 从 localStorage 加载会话状态
const loadSessionFromStorage = (): Partial<LockSessionState> => {
  try {
    const data = localStorage.getItem(LOCK_SESSION_KEY)
    if (data) {
      // 安全措施：启动时不恢复解锁状态，每次启动都需要重新验证密码
      // 这样确保重启应用后上锁笔记仍需要密码才能访问
      localStorage.removeItem(LOCK_SESSION_KEY)
    }
  } catch (error) {
    console.error('[LockSession] 加载会话状态失败:', error)
  }
  return {}
}

// 保存会话状态到 localStorage
const saveSessionToStorage = (state: LockSessionState) => {
  try {
    const dataToSave = {
      isGloballyUnlocked: state.isGloballyUnlocked,
      unlockedAt: state.unlockedAt,
      lastActivityAt: state.lastActivityAt,
    }
    localStorage.setItem(LOCK_SESSION_KEY, JSON.stringify(dataToSave))
  } catch (error) {
    console.error('[LockSession] 保存会话状态失败:', error)
  }
}

// 初始状态
const initialState: LockSessionState = {
  isGloballyUnlocked: false,
  unlockedAt: null,
  lastActivityAt: null,
  config: DEFAULT_CONFIG,
  ...loadSessionFromStorage(),
}

const lockSessionSlice = createSlice({
  name: 'lockSession',
  initialState,
  reducers: {
    // 全局解锁
    unlockGlobally: (state) => {
      const now = Date.now()
      state.isGloballyUnlocked = true
      state.unlockedAt = now
      state.lastActivityAt = now
      saveSessionToStorage(state)
      console.log('[LockSession] 全局解锁，时间:', new Date(now).toLocaleString())
    },
    
    // 全局锁定
    lockGlobally: (state) => {
      state.isGloballyUnlocked = false
      state.unlockedAt = null
      state.lastActivityAt = null
      saveSessionToStorage(state)
      console.log('[LockSession] 全局锁定')
    },
    
    // 更新活动时间
    updateActivity: (state) => {
      if (state.isGloballyUnlocked) {
        state.lastActivityAt = Date.now()
        saveSessionToStorage(state)
      }
    },
    
    // 检查并处理超时
    checkTimeout: (state) => {
      if (!state.isGloballyUnlocked) return
      
      const now = Date.now()
      const { idleTimeout, absoluteTimeout } = state.config
      
      // 检查空闲超时
      if (state.lastActivityAt && now - state.lastActivityAt > idleTimeout) {
        console.log('[LockSession] 空闲超时，自动锁定')
        state.isGloballyUnlocked = false
        state.unlockedAt = null
        state.lastActivityAt = null
        saveSessionToStorage(state)
        return
      }
      
      // 检查绝对超时
      if (state.unlockedAt && now - state.unlockedAt > absoluteTimeout) {
        console.log('[LockSession] 绝对超时，自动锁定')
        state.isGloballyUnlocked = false
        state.unlockedAt = null
        state.lastActivityAt = null
        saveSessionToStorage(state)
        return
      }
    },
    
    // 更新配置
    updateConfig: (state, action: PayloadAction<Partial<LockSessionState['config']>>) => {
      state.config = { ...state.config, ...action.payload }
    },
    
    // 重置会话（用于应用启动时检查）
    resetSession: (state) => {
      state.isGloballyUnlocked = false
      state.unlockedAt = null
      state.lastActivityAt = null
      saveSessionToStorage(state)
    },
  },
})

// 导出 actions
export const {
  unlockGlobally,
  lockGlobally,
  updateActivity,
  checkTimeout,
  updateConfig,
  resetSession,
} = lockSessionSlice.actions

// 导出 selectors
export const selectIsGloballyUnlocked = (state: { lockSession: LockSessionState }) => 
  state.lockSession.isGloballyUnlocked

export const selectLockSession = (state: { lockSession: LockSessionState }) => 
  state.lockSession

export const selectRemainingTime = (state: { lockSession: LockSessionState }): number => {
  const { isGloballyUnlocked, unlockedAt, config } = state.lockSession
  if (!isGloballyUnlocked || !unlockedAt) return 0
  
  const elapsed = Date.now() - unlockedAt
  const remaining = config.absoluteTimeout - elapsed
  return Math.max(0, remaining)
}

export const selectIdleRemainingTime = (state: { lockSession: LockSessionState }): number => {
  const { isGloballyUnlocked, lastActivityAt, config } = state.lockSession
  if (!isGloballyUnlocked || !lastActivityAt) return 0
  
  const elapsed = Date.now() - lastActivityAt
  const remaining = config.idleTimeout - elapsed
  return Math.max(0, remaining)
}

export const selectIsWarningNeeded = (state: { lockSession: LockSessionState }): boolean => {
  const remaining = selectRemainingTime(state)
  const idleRemaining = selectIdleRemainingTime(state)
  const { config } = state.lockSession
  
  // 如果任一超时即将到期，显示警告
  return remaining < config.warningBeforeTimeout || idleRemaining < config.warningBeforeTimeout
}

export default lockSessionSlice.reducer
